/**
 * EntityHeader — the detail-page identity header (UX-13 S0, promoted
 * per the Phase 1 direction §6): an entity is a header, not a page
 * title, so detail pages compose this below a headerless PageShell
 * rather than using the shell's string `title`.
 *
 * Anatomy: eyebrow ("Organization" / "Contact" — 11.5px uppercase
 * tracked sage-400) · identity slot (IdentityChip at 58 or an initials
 * avatar) · Playfair name at text-2xl sage-800 with the badge slot
 * inline (Recommended) · a meta line in ink-soft (LastEdited) · the
 * action row (Call/Email/Website quiet sage buttons; the suggest CTA
 * in the ruled gold-punctuation treatment — the CTA styling itself
 * lives with its consumers/Button, not here).
 *
 * Presentation only: slots carry everything variable; no data access,
 * no gating (composers gate). Names that can truncate carry `title`
 * per the never-silently rule.
 */

import { type ReactNode } from 'react';

export interface EntityHeaderProps {
  /** "Organization" / "Contact" — the entity-type kicker. */
  eyebrow: string;
  /**
   * On-band composition (UX design pass 2026-07-06): when the header sits
   * on a colored band, the sage-400 kicker can fall under AA — the band's
   * composer passes its family's deep value (the craft-floor "step to the
   * deep value" rule). Merges onto the eyebrow classes.
   */
  eyebrowClassName?: string;
  /**
   * On-band name color (UX sweep 2026-07-10): the default sage-800 name
   * is invisible on the forest band, so a dark-band composer passes its
   * on-dark value (text-cream). Merges onto the h1 in place of the
   * default color — same contract as eyebrowClassName.
   */
  nameClassName?: string;
  /** IdentityChip (58) or an initials avatar. */
  identity: ReactNode;
  name: string;
  /**
   * Editable-name composition (UX-13 S3 additive, the deniedContent
   * pattern): both real detail pages render the name as an inline
   * EditableField, which a plain string cannot host. When present it
   * renders inside the h1 in place of the truncate span and the
   * composer owns truncation/title semantics; `name` still names the
   * entity for the a11y/`title` fallback and stays the simple default
   * for read-only composers.
   */
  nameSlot?: ReactNode;
  /** Inline after the name (e.g. RecommendedBadge). */
  badge?: ReactNode;
  /** The ink-soft meta line (e.g. LastEdited). */
  meta?: ReactNode;
  /** Call / Email / Website + the suggest CTA. */
  actions?: ReactNode;
}

export function EntityHeader({
  eyebrow,
  eyebrowClassName,
  nameClassName,
  identity,
  name,
  nameSlot,
  badge,
  meta,
  actions,
}: EntityHeaderProps): ReactNode {
  return (
    <header data-testid="entity-header" className="flex items-start gap-4">
      {identity}
      <div className="min-w-0 flex-1">
        <p
          className={[
            // Ratified scale (2026-08-25): the eyebrow is a LABEL — one geometry
            // across the head; only the ink varies by ground.
            'text-label tracking-label font-semibold uppercase',
            eyebrowClassName ?? 'text-sage-400',
          ].join(' ')}
        >
          {eyebrow}
        </p>
        <h1
          className={[
            'font-display text-2xl flex items-center gap-2 flex-wrap min-w-0',
            nameClassName ?? 'text-sage-800',
          ].join(' ')}
        >
          {nameSlot ?? (
            <span className="truncate" title={name}>
              {name}
            </span>
          )}
          {badge}
        </h1>
        {meta && <div className="text-xs text-ink-soft mt-0.5">{meta}</div>}
        {actions && (
          <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="entity-header-actions">
            {actions}
          </div>
        )}
      </div>
    </header>
  );
}
